import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { createConsultationBedTransfersApi } from "../../../services/consultation.service";
import { toast } from "react-toastify";
import { getUserApi } from "../../../services/user.service";
import { useGetApi } from "../../../hooks/useGetApi";
import { getWardApi } from "../../../services/ward.service";
import { getNursingBedNumberApi } from "../../../services/bed-number.service";

const BedTransferModal = ({ open, onClose, selectedRow, refetch }) => {
  const initialState = {
    visit: selectedRow?.occupancyDetails?.visitId || "",
    patient: selectedRow?.occupancyDetails?.patientId || "",
    currentWard: selectedRow?.wardId || "",
    currentBed: selectedRow?.bedNumberId || "",
    requestedWard: null,
    requestedBed: null,
    requestedBy: null,
    transferReason: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const { dataList: wardList } = useGetApi({
    apiFunction: getWardApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "wards",
  });

  const { dataList: bedNumberList } = useGetApi({
    apiFunction: formData?.requestedWard?._id ? getNursingBedNumberApi : null,
    params: { 
      limit: Number.MAX_SAFE_INTEGER,
      bedStatus: "Available",
      wardName: formData?.requestedWard?.wardName
    },
    dependencies: [formData?.requestedWard?._id],
    dataKey: "bedNumbers",
  });

  const filteredWardList = wardList?.filter(
    (ward) => selectedRow?.wardId !== ward?._id
  );
  
  // Filter bed numbers to exclude current bed (API already filters by ward and status)
  const filteredBedNumberList = bedNumberList?.filter(
    (bedNumber) => bedNumber._id !== selectedRow?.bedNumberId
  );

  const { dataList: userList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevValue) => {
      return {
        ...prevValue,
        [name]: value,
        // Clear bed selection when ward changes
        ...(name === "requestedWard" && { requestedBed: null }),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    const response = await createConsultationBedTransfersApi({
      ...formData,
      requestedWard: formData?.requestedWard?._id,
      requestedBed: formData?.requestedBed?._id,
      requestedBy: formData?.requestedBy?._id,
    });
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(response?.message || `Bed Transfer initiated successfully`);
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    setFormData((prevValue) => ({
      ...prevValue,
      visit: selectedRow?.occupancyDetails?.visitId || "",
      patient: selectedRow?.occupancyDetails?.patientId || "",
      currentWard: selectedRow?.wardId || "",
      currentBed: selectedRow?.bedNumberId || "",
    }));
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit}>
          {/* dialog title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "20px",
            }}
          >
            <Typography variant="h6" sx={{ color: "#5D6A82" }}>
              Ward Transfer Details
            </Typography>
            <Close
              onClick={onClose}
              sx={{ cursor: "pointer", color: "#5D6A82" }}
            />
          </Box>

          <Grid
            container
            spacing={3}
            sx={{
              mb: "40px",
            }}
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                options={filteredWardList || []}
                getOptionLabel={(option) => option?.wardName || ""}
                renderInput={(params) => (
                  <TextField {...params} label="New Ward" required fullWidth />
                )}
                value={formData?.requestedWard || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "requestedWard", value: newValue },
                  })
                }
                sx={{ background: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                options={filteredBedNumberList || []}
                getOptionLabel={(option) => option?.bedNumber || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bed Number"
                    required
                    fullWidth
                  />
                )}
                value={formData?.requestedBed || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "requestedBed", value: newValue },
                  })
                }
                sx={{ background: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                options={userList || []}
                getOptionLabel={(option) =>
                  `${option?.firstName || ""} ${option?.lastName || ""} (${
                    option?.userId || ""
                  })`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Approval Nurse Name"
                    fullWidth
                    required
                  />
                )}
                value={formData?.requestedBy || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "requestedBy", value: newValue },
                  })
                }
                sx={{ background: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                label="Reason Of Transfer"
                name="transferReason"
                value={formData?.transferReason || ""}
                onChange={handleChange}
                required
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>
          </Grid>

          {/* submit button */}
          <Box sx={{ my: "20px", textAlign: "center" }}>
            <Button
              type="submit"
              variant="contained"
              sx={{ width: "150px", textTransform: "none" }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : `Submit`}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BedTransferModal;
